import React, { useState, useEffect, useMemo } from "react";
import ReactTooltip from "react-tooltip";
import { EVENTS_URL } from "../env";
import styled from "styled-components";
import { Colors } from "@sellout/ui/build/Colors";
import Icon, { Icons } from "@sellout/ui/build/components/Icon";
import { IEventGraphQL } from "@sellout/models/.dist/interfaces/IEvent";
import DetailsCard from "../elements/DetailsCard";
import Label from "@sellout/ui/build/components/Label";
import Button, { ButtonTypes, ButtonStates } from "@sellout/ui/build/components/Button";
import { useMutation, useQuery } from "@apollo/react-hooks";
import GENERATE_EVENT_QR_CODE from "@sellout/models/.dist/graphql/mutations/generateEventQRCode.mutation";
import UPDATE_EVENT from "@sellout/models/.dist/graphql/mutations/updateEvent.mutation";
import LIST_TAGS from "@sellout/models/.dist/graphql/queries/tags.query";
import ITag from "@sellout/models/.dist/interfaces/ITag";
import useEvent from "../hooks/useEvent.hook";
import GET_EVENT from "@sellout/models/.dist/graphql/queries/event.query";
import { useDispatch } from "react-redux";
import * as AppActions from "../redux/actions/app.actions";
import * as EventActions from "../redux/actions/event.actions";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import Loader, { LoaderSizes } from "@sellout/ui/build/components/Loader";
import { ModalTypes } from "../components/modal/Modal";
import * as RemoveUtil from "../utils/RemoveUtil";
import Select, { MultiValue } from "react-select";

const Section = styled.div`
  margin-right: 20px;
`;

const FlexContainer = styled.div`
  display:flex; 
`;

const Spacer = styled.div`
  height: 25px;
`;

const CopiedSpan = styled.span`
  display: flex;
  align-items: center;
`;
const ImgSection = styled.div`
`;

const DownloadQr = styled.div`
`;

const GenerateLink = styled.div`
`;

const AnchorDiv = styled.div`
  margin-top: 10px;
`;

const ListingSection = styled.div`
  margin-top: 25px;
`;

const StatusText = styled.div`
  font-size: 1.4rem;
  color: ${Colors.Grey2};
  margin-top: 10px;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  margin-bottom: 10px;
`;

const MultiSelectWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
`;

const StyledMultiSelect = styled(Select)`
  font-family: "neue-haas-grotesk-display", sans-serif;

  .select__control {
    border-radius: 8px;
    border: 1px solid ${Colors.Grey5};
    background-color: ${Colors.White};
    min-height: 40px;
    box-shadow: none;
    cursor: pointer;
    &:hover {
      border-color: ${Colors.Grey4};
    }
  }

  .select__control--is-focused {
    border-color: ${Colors.Orange};
    box-shadow: 0 0 0 1px ${Colors.Orange};
  }

  .select__multi-value {
    background-color: ${Colors.Orange};
    border-radius: 12px;
    padding: 2px 6px;
  }

  .select__multi-value__label {
    color: ${Colors.White};
    font-size: 1.2rem;
    font-weight: 500;
    padding: 2px 4px;
  }

  .select__multi-value__remove {
    color: ${Colors.White};
    cursor: pointer;
    border-radius: 0 10px 10px 0;
    &:hover {
      background-color: rgba(0, 0, 0, 0.15);
      color: ${Colors.White};
    }
  }

  .select__menu {
    border-radius: 8px;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }

  .select__option {
    font-size: 1.3rem;
    color: ${Colors.Grey1};
    cursor: pointer;
  }

  .select__option--is-focused {
    background-color: ${Colors.Grey7};
  }

  .select__option--is-selected {
    background-color: ${Colors.Orange};
    color: ${Colors.White};
  }

  .select__placeholder {
    font-size: 1.3rem;
    color: ${Colors.Grey4};
  }
`;

const SaveTagsContainer = styled.div`
  margin-top: 12px;
`;

export enum EventLinksCardTextEnum {
  Title = "Event Links",
  UrlLabel = "URL for this event",
  QrLabel = "Event QR code",
  DownloadQr = "Download QR code",
  Copied = "Copied!",
  CopyTip = "Copy",
  EventQrCode = "Event QR Code",
  GenerateQrCode = "Generate Event QR Code",
  ListEventLabel = "List event on Sellout.io website",
  RemoveFromWebsite = "Remove from website",
  ListOnWebsite = "List on website",
  StatusCurrentlyListing = "You are ",
  StatusNotListing = "NOT ",
  StatusCurrentlyListingEnd = "currently listing this event on events.sellout.io.",
  StatusChangeInstructions = "Click the button above to change this status.",
  AssignTagsLabel = "Assign Tags to Event",
  TagsSavedSuccess = "Event tags saved successfully",
  NoTagsFound = "No tags found",
  PublishToAssignTags = "Publish this event to events.sellout.io to assign tags.",
}

const getEventBaseUrl = () => {
  return EVENTS_URL || "";
};

const cleanEventForUpdate = (event: IEventGraphQL) => {
  let cleanedEvent = { ...event } as any;
  cleanedEvent = RemoveUtil.removeEmpty(cleanedEvent);
  delete cleanedEvent.organization;
  delete cleanedEvent.fees;
  delete cleanedEvent.artists;
  delete cleanedEvent.createdAt;
  delete cleanedEvent.venue;
  delete cleanedEvent.webFlowEntity;
  delete cleanedEvent.hasOrders;
  delete cleanedEvent.published;
  delete cleanedEvent.analytics;
  delete cleanedEvent.seatingPublicKey;
  delete cleanedEvent.active;
  delete cleanedEvent.subscription;
  delete cleanedEvent.guestList;
  return cleanedEvent;
};

interface ITagOption {
  label: string;
  value: string;
}

const EventLinksCard: React.FC<{ event?: IEventGraphQL }> = () => {
  const [copied, setCopied] = useState(false);
  const { event, eventId } = useEvent();
  const dispatch = useDispatch();
  
  const { data: tagsData } = useQuery(LIST_TAGS);
  const allTags: ITag[] = tagsData?.tags || [];

  const initialTagIds: string[] = (event?.tagIds as string[]) || [];
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTagIds);

  useEffect(() => {
    setSelectedTagIds((event?.tagIds as string[]) || []);
  }, [event]);

  const tagOptions: ITagOption[] = useMemo(
    () => allTags.map((t) => ({ label: t.name, value: t._id as string })),
    [allTags]
  );

  const selectedOptions = useMemo(
    () => tagOptions.filter((opt) => selectedTagIds.includes(opt.value)),
    [tagOptions, selectedTagIds]
  );

  const hasTagsChanged = useMemo(
    () =>
      JSON.stringify([...selectedTagIds].sort()) !==
      JSON.stringify([...initialTagIds].sort()),
    [selectedTagIds, initialTagIds]
  );

  const isListedOnWebsite = Boolean(event?.publishable);
  const savedEventUrlStub = event?.stub;

  const [generateQRCode, { data, loading: qrLoading }] = useMutation(GENERATE_EVENT_QR_CODE, {
    refetchQueries: [
      {
        query: GET_EVENT,
        variables: { eventId }
      }
    ],
    onError(error) {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error
        )
      );
    }
  });

  const [updateEvent, { loading: updateTagLoading }] = useMutation(UPDATE_EVENT, {
    refetchQueries: [
      {
        query: GET_EVENT,
        variables: { eventId }
      }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      if (data?.updateEvent) {
        dispatch(EventActions.cacheEvents([data?.updateEvent]));
      }
      dispatch(
        AppActions.showNotification(
          EventLinksCardTextEnum.TagsSavedSuccess,
          AppNotificationTypeEnum.Success
        )
      );
    },
    onError: (error) => {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error
        )
      );
    }
  });

  if (!event) return null;

  const qrCodeUrl = event?.qrCodeUrl;
  const eventUrl = getEventBaseUrl();
  const eventUrlStub = isListedOnWebsite && savedEventUrlStub 
    ? `${eventUrl}/${savedEventUrlStub}`
    : `${eventUrl}/${event?._id}`;

  const copyToClipBoard = (copyMe: string) => {
    try {
      navigator.clipboard.writeText(copyMe);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setCopied(false);
    }
  };

  const handleGenerateQRCode = () => {
    generateQRCode({ variables: { eventId } });
  };

  const handleListEventToggle = () => {
    dispatch(AppActions.pushModal(
      ModalTypes.ListEvent,
      {
        onConfirm: () => {
        }
      }
    ));
  };

  const handleSaveTags = () => {
    const cleanedEvent = cleanEventForUpdate(event as IEventGraphQL);
    updateEvent({
      variables: {
        event: {
          ...cleanedEvent,
          tagIds: selectedTagIds,
        }
      }
    });
  };


  return (
    <DetailsCard
      title={EventLinksCardTextEnum.Title}
      headerIcon={Icons.LinkRegular}
      width="600px"
    >
      <Section>
        <Label text={EventLinksCardTextEnum.UrlLabel} />
        <FlexContainer style={{ alignItems: "center" }}>
          <a
            href={eventUrlStub}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginRight: "10px", fontSize: "1.4rem" }}
          >
            {eventUrlStub}
          </a>
          {copied ? (
            <CopiedSpan>
              {EventLinksCardTextEnum.Copied} {" "}
              <Icon icon={Icons.CheckCircle} color={Colors.Green} size={15} />
            </CopiedSpan>
          ) : (
            <span data-tip={EventLinksCardTextEnum.CopyTip}>
              <Icon
                icon={Icons.CopyRegular}
                color={Colors.Grey4}
                hoverColor={Colors.Grey1}
                size={16}
                onClick={() => copyToClipBoard(eventUrlStub)}
              />
              <ReactTooltip effect="solid" />
            </span>
          )}
        </FlexContainer>
      </Section>

      <Spacer />

      <Section>
        <Label text={EventLinksCardTextEnum.QrLabel} />
        {(data?.generateEventQRCode||qrCodeUrl) ? (
          <ImgSection>
            <img
              src={data?.generateEventQRCode || qrCodeUrl}
              alt={EventLinksCardTextEnum.EventQrCode}
              style={{ width: "150px", height: "150px" }}
            />
            <DownloadQr>
              <a href={data?.generateEventQRCode || qrCodeUrl} target="_blank" rel="noopener noreferrer">
                {EventLinksCardTextEnum.DownloadQr}
              </a>
            </DownloadQr>
          </ImgSection>
        ) : (
          <GenerateLink>
            <AnchorDiv>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  handleGenerateQRCode();
                }}
                style={{ 
                  color: Colors.Blue, 
                  textDecoration: "underline",
                  cursor: qrLoading ? "not-allowed" : "pointer",
                  opacity: qrLoading ? 0.6 : 1
                }}
              >
               {qrLoading ? <Loader size={LoaderSizes.Small} color={Colors.Orange} /> : EventLinksCardTextEnum.GenerateQrCode}
              </a>
            </AnchorDiv>

          </GenerateLink>
        )}
      </Section>

      <Spacer />

      <Section>
        <Label text={EventLinksCardTextEnum.AssignTagsLabel} />
        {isListedOnWebsite ? (
          allTags.length > 0 ? (
            <>
              <MultiSelectWrapper>
                <StyledMultiSelect
                  classNamePrefix="select"
                  isMulti
                  isSearchable
                  placeholder="Select tags..."
                  options={tagOptions}
                  value={selectedOptions}
                  onChange={(selected: MultiValue<ITagOption>) => {
                    setSelectedTagIds(selected ? (selected as ITagOption[]).map((opt) => opt.value) : []);
                  }}
                />
              </MultiSelectWrapper>
              <SaveTagsContainer>
                <Button
                  type={ButtonTypes.Thin}
                  text="SAVE TAGS"
                  state={hasTagsChanged ? ButtonStates.Active : ButtonStates.Disabled}
                  onClick={handleSaveTags}
                  loading={updateTagLoading}
                />
              </SaveTagsContainer>
            </>
          ) : (
            <StatusText>
              {EventLinksCardTextEnum.NoTagsFound}
            </StatusText>
          )
        ) : (
          <StatusText>
            {EventLinksCardTextEnum.PublishToAssignTags}
          </StatusText>
        )}
      </Section>

      <ListingSection>
        <Label text={EventLinksCardTextEnum.ListEventLabel} />
        <ButtonContainer>
          <Button
            type={ButtonTypes.Thin}
            text={isListedOnWebsite ? EventLinksCardTextEnum.RemoveFromWebsite : EventLinksCardTextEnum.ListOnWebsite}
            state={ButtonStates.Active}
            onClick={handleListEventToggle}
          />
        </ButtonContainer>
        <StatusText>
          {EventLinksCardTextEnum.StatusCurrentlyListing}{isListedOnWebsite ? '' : EventLinksCardTextEnum.StatusNotListing}{EventLinksCardTextEnum.StatusCurrentlyListingEnd} {EventLinksCardTextEnum.StatusChangeInstructions}
        </StatusText>
      </ListingSection>
    </DetailsCard>
  );
};

export default EventLinksCard;
 